import {MolecularGraph} from "../../graph/MolecularGraph"
import type {GraphAtom} from "../../graph/GraphAtom"
import {chainFinder, chainFinderOptions} from "../../util/chainFinder";
import {Feature, Features} from "../../feature";
import {extractorUtil} from "../../util/extractorUtil";

export const chainExtractor = {
    extract: (graph: MolecularGraph, options?: chainFinderOptions) => {
        const finalOptions = {onlyCarbonChains: options?.onlyCarbonChains, includeSubChains: true}
        // Carbon chains terminate at carbons with only one connection to another carbon.
        // We can collect those nodes and make an NxN map of connections. We can find the
        // longest chain between two terminal nodes. Once we do that, we can then find the
        // rest of the chains.
        // Sort the chains by length. Longest first
        const rawChains = chainFinder.findChains(graph, finalOptions);
        if (rawChains.length === 0) {
            return [] as Feature[];
        }

        // 06/18/2025 (What to do about NO2)
        // rawChains is of heavy atoms. We want to trim the chains so that they don't
        // terminate with a non-carbon atom.
        const terminalCarbonChains = rawChains.map(chain => {
            while (chain.length > 0 && chain[0].atomicNumber !== 6) {
                chain.shift();
            }
            while (chain.length > 0 && chain[chain.length - 1].atomicNumber !== 6) {
                chain.pop();
            }
            return chain;
        })

        // NOTE: In the future, we're gonna have to use a more complex sort that
        // looks at substituent priorities.
        const sortedChains = terminalCarbonChains.sort((a, b) => b.length - a.length);
        if (sortedChains.length === 0) {
            return [] as Feature[];
        }

        // One additional sorting is which chain gives us the most constituents
        // If there are multiple chains that have the longest length, we want to
        // choose the chain that would give us the most sub-chains.
        // NOTE: This is not quite the same as multiple constituents, but I think
        // the argument will hold (at least until we're shown otherwise)
        const lengthOfLongestChain = sortedChains[0].length;
        const contendersForLongestChain = sortedChains.filter(c => c.length === lengthOfLongestChain)

        // Put the contender chain on top and then filter. The second copy will
        // be filtered out so there's only one.
        const chainOptions = contendersForLongestChain.map(c => chainExtractor.filterChains([c, ...sortedChains]));

        // According to Klein, we want to choose the chain that will give back the most substituents.
        // Since subchains were cataloged in findChains, this means that the largest chain set is the best
        const sortedOptions = chainOptions.sort((a, b) => b.length - a.length);
        return chainExtractor.extractFeatures(sortedOptions[0], graph)
    },
    filterChains: (chains: GraphAtom[][]) => {
        // All of the chains can only use an atom once. We only care about longest
        // chains. We should filter out the sub-optimal chains.
        const usedAtoms: GraphAtom[] = [];
        const candidateChains = chains.slice();
        const finalChains: GraphAtom[][] = [];
        while (candidateChains.length > 0) {
            // We want to grab from the top of the array since the array should be sorted
            const chain = candidateChains.shift()!;
            const isValid = chain.length > 0 && chain.every(a => !usedAtoms.includes(a));
            if (isValid) {
                finalChains.push(chain);
                usedAtoms.push(...chain);
            }
        }

        return finalChains;
    },
    extractFeatures: (chains: GraphAtom[][], graph: MolecularGraph) => {
        return chains.map(chain => chainExtractor.extractFeature(chain, graph))
    },
    extractFeature: (chain: GraphAtom[], graph: MolecularGraph) => {
        let name = `${Features.chain}.${chain.length}`;

        // Small chains get special names (if they match criteria).
        const numHs = chainExtractor.countHsInChain(chain, graph)
        if (chain.length === 1 && numHs === 3) {
            name = Features.methyl
        } else if (chain.length === 2 && numHs === 5) {
            name = Features.ethyl
        }

        // PIPH-217: enumerate external connections at every chain atom so mid-chain
        // attachments (e.g. another chain bonded to a mid carbon) are captured.
        // Mirrors ringExtractor; previously only terminal atoms were considered.
        const r = Array.from(new Set(chain.map(atom => extractorUtil.getFilteredNonHConnectedAtoms(atom, graph, chain)).flat()));

        return {name, atoms: chain, connections: r} as Feature
    },
    countHsInChain: (chain: GraphAtom[], graph: MolecularGraph) => {
        // PIPH-116: Use getTotalHydrogenCount to include both explicit H bonds
        // and implicit H counts (from workspace serialization with hidden hydrogens)
        return chain.reduce((acc, a) => acc += extractorUtil.getTotalHydrogenCount(a, graph), 0);
    }
}
